import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, X, Instagram, Linkedin, Youtube } from 'lucide-react';

import { Logo } from '../ui/Logo';

export default function Footer() {
  const { t } = useTranslation();
  
  return (
    <footer className="bg-neutral-900 text-white m-[5px] p-[5px]">
      <div className="container mx-auto p-[5px]">
        {/* Reassurance block */}
        <div className="mb-[5px] rounded-2xl bg-neutral-800/80 border border-neutral-700/80 p-[5px] text-center">
          <h3 className="text-lg font-semibold text-white mb-[5px]">Need help finding a home?</h3>
          <p className="text-neutral-300 mb-[5px] max-w-xl mx-auto">
            Reach out and our team will guide you step by step.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-5 py-2.5 bg-white text-neutral-900 font-medium rounded-lg hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-900"
          >
            Contact us
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-[5px]">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="inline-flex items-center mb-[5px] py-0">
              <Logo className="h-[10rem] w-auto max-w-full" pictureSizes="240px" />
            </Link>
            <p className="text-neutral-300 mb-[5px] text-sm">
              {t('site.tagline')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href="https://www.facebook.com/profile.php?id=61579691546702"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 stroke-[1.85]" aria-hidden />
              </a>
              <a
                href="https://x.com/rentabahay"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="X"
              >
                <X className="h-5 w-5 stroke-[1.85]" aria-hidden />
              </a>
              <a
                href="https://www.instagram.com/rentabahay/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 stroke-[1.85]" aria-hidden />
              </a>
              <a
                href="https://www.linkedin.com/company/rentabahay"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5 stroke-[1.85]" aria-hidden />
              </a>
              <a
                href="https://www.youtube.com/@RentaBahay"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5 stroke-[1.85]" aria-hidden />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-[5px]">Properties</h4>
            <ul className="space-y-2">
              <li><Link to="/properties" className="text-neutral-300 hover:text-white">All Rentals</Link></li>
              <li><Link to="/properties?type=apartment" className="text-neutral-300 hover:text-white">Apartments</Link></li>
              <li><Link to="/properties?type=house" className="text-neutral-300 hover:text-white">Houses</Link></li>
              <li><Link to="/properties?type=condo" className="text-neutral-300 hover:text-white">Condominiums</Link></li>
              <li><Link to="/properties?type=commercial" className="text-neutral-300 hover:text-white">Commercial</Link></li>
            </ul>
          </div>
          
          {/* Locations */}
          <div>
            <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-[5px]">Popular Locations</h4>
            <ul className="space-y-2">
              <li><Link to="/properties?location=manila" className="text-neutral-300 hover:text-white">Manila</Link></li>
              <li><Link to="/properties?location=quezon-city" className="text-neutral-300 hover:text-white">Quezon City</Link></li>
              <li><Link to="/properties?location=cebu" className="text-neutral-300 hover:text-white">Cebu</Link></li>
              <li><Link to="/properties?location=davao" className="text-neutral-300 hover:text-white">Davao</Link></li>
              <li><Link to="/properties?location=bgc" className="text-neutral-300 hover:text-white">BGC</Link></li>
            </ul>
          </div>
          
          {/* Company */}
          <div>
            <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-[5px]">Company</h4>
            <ul className="space-y-2">
              <li><Link to="/#how-it-works" className="text-neutral-300 hover:text-white">How It Works</Link></li>
              <li><Link to="/about" className="text-neutral-300 hover:text-white">{t('footer.about')}</Link></li>
              <li><Link to="/careers" className="text-neutral-300 hover:text-white">{t('footer.careers')}</Link></li>
              <li><Link to="/help" className="text-neutral-300 hover:text-white">{t('footer.help')}</Link></li>
              <li><Link to="/terms" className="text-neutral-300 hover:text-white">{t('footer.terms')}</Link></li>
              <li><Link to="/privacy" className="text-neutral-300 hover:text-white">{t('footer.privacy')}</Link></li>
              <li><Link to="/contact" className="text-neutral-300 hover:text-white">{t('footer.contact')}</Link></li>
            </ul>
          </div>
          
          {/* Blog */}
          <div>
            <h4 className="font-semibold text-white text-sm uppercase tracking-wider mb-[5px]">Blog</h4>
            <ul className="space-y-2">
              <li><Link to="/blog" className="text-neutral-300 hover:text-white">All Articles</Link></li>
              <li><Link to="/blog?category=Rental Guides" className="text-neutral-300 hover:text-white">Rental Guides</Link></li>
              <li><Link to="/blog?category=Market Analysis" className="text-neutral-300 hover:text-white">Market Analysis</Link></li>
              <li><Link to="/blog?category=Regional Markets" className="text-neutral-300 hover:text-white">Regional Markets</Link></li>
              <li><Link to="/blog?category=Property Management" className="text-neutral-300 hover:text-white">Property Management</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-[5px] pt-[5px] border-t border-neutral-700 text-center text-neutral-400 text-sm">
          <p>© {new Date().getFullYear()} {t('site.name')}. All rights reserved. | Powered by <a href="https://realboss.ai" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-300">RealBoss.AI</a></p>
          <p className="mt-[5px]">
            The premier real estate rental platform in the Philippines.
          </p>
        </div>
      </div>
    </footer>
  );
}